import { useEffect, useRef, useState } from "react";

function DeliveryLazyImage({
    src,
    alt,
    className,
    layout = "grid",
    onClick,
    longPressHandlers,
    interactive = true,
}) {
    const containerRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [imageReady, setImageReady] = useState(false);
    const [naturalAspectRatio, setNaturalAspectRatio] = useState(null);
    const isWaterfall = layout === "waterfall";

    useEffect(() => {
        const node = containerRef.current;
        if (!node) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: isWaterfall ? "480px 0px" : "240px 0px", threshold: 0.01 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [isWaterfall]);

    useEffect(() => {
        if (!visible || !src) {
            return undefined;
        }

        let cancelled = false;
        const probe = new Image();

        probe.onload = () => {
            if (cancelled) {
                return;
            }
            if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
                setNaturalAspectRatio(`${probe.naturalWidth} / ${probe.naturalHeight}`);
            }
            setImageReady(true);
        };

        probe.onerror = () => {
            if (!cancelled) {
                setImageReady(true);
            }
        };

        probe.src = src;

        return () => {
            cancelled = true;
            probe.onload = null;
            probe.onerror = null;
        };
    }, [src, visible]);

    const { onClick: longPressClick, ...restLongPressHandlers } = longPressHandlers || {};

    const handleClick = (event) => {
        if (longPressClick) {
            longPressClick(event);
            if (event.defaultPrevented) {
                return;
            }
        }
        if (!interactive || !onClick) {
            return;
        }
        onClick(event);
    };

    const frameStyle =
        isWaterfall && naturalAspectRatio ? { aspectRatio: naturalAspectRatio } : undefined;

    return (
        <div
            ref={containerRef}
            className={`delivery-browser__lazy-wrap delivery-browser__lazy-wrap--${layout}${
                interactive && onClick ? " delivery-browser__lazy-wrap--interactive" : ""
            }`}
            {...restLongPressHandlers}
            onClick={handleClick}
            onKeyDown={
                interactive && onClick
                    ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onClick(event);
                          }
                      }
                    : undefined
            }
            role={interactive && onClick ? "button" : undefined}
            tabIndex={interactive && onClick ? 0 : undefined}
        >
            <div className="delivery-browser__thumb-frame" style={frameStyle}>
                {visible && imageReady ? (
                    <img
                        src={src}
                        alt={alt}
                        className={className}
                        loading={isWaterfall ? "eager" : "lazy"}
                        decoding="async"
                        draggable={false}
                    />
                ) : (
                    <div
                        className={`delivery-browser__thumb-placeholder${
                            isWaterfall ? " delivery-browser__thumb-placeholder--waterfall" : ""
                        }`}
                        aria-hidden="true"
                    />
                )}
            </div>
        </div>
    );
}

export default DeliveryLazyImage;
